/**
 * Drawing the things a battle is fought around.
 *
 * `src/sim/obstacles.ts` decides where the trees and rocks are, how wide each
 * one is, and what it blocks. This decides what it *looks* like, and the split
 * is the project's usual one: the list is hashed state rolled from the battle's
 * seed, and nothing here can move a boulder — `fieldObstacles(w)` hands out
 * copies for exactly that reason.
 *
 * ── One asset per obstacle, sized by its own radius ─────────────────────────
 *
 * A `FieldObstacle` is `{kind, x, y, r}` — **one circle per clump, not one per
 * tile** — and `r` runs from about 1.5 to 5.4 tiles. So the art is not placed
 * at a fixed size: a `field_boulder` at r = 1.5 and one at r = 3.7 are the same
 * file drawn at different scales, which is what stops thirty pieces of scatter
 * from being thirty copies of one picture. That is also why the props are
 * authored square with a marked ground line rather than as tile art.
 *
 * ── Why the variant is hashed and not cycled ────────────────────────────────
 *
 * `list[i % variants.length]` puts the same tree next to the same tree
 * whenever the generator happens to emit two of a kind in a row, which it does
 * — the scatter is rolled kind by kind. A hash of the index scatters them, and
 * being a pure function of the index it is stable: the same battlefield draws
 * the same trees on reload, on a replay, and after the camera has been away.
 *
 * The hash takes its own salt, per the rule the four `Rng`s in the simulation
 * follow and `render/chatter.ts` repeats.
 */

import { ObstacleKind, type FieldObstacle, type ObstacleKindId } from '../sim/types.ts';
import { hash2 } from '../core/math.ts';

const SALT_VARIANT = 0x6a37;

/**
 * Which baked stills stand for which kind of obstacle.
 *
 * The assignment is the **art's** reading of the mechanic, and the mechanic is
 * what decides it rather than the name: `Thicket` is "no trunk, all cover —
 * walkable, and nothing shoots cleanly through it", so scrub, deadfall, stakes,
 * bones and a stump all qualify and a boulder does not. `Rock` is solid all the
 * way across and stops an arrow dead, which a cairn does and a bush does not.
 *
 * If the generator gains a new `field_*` still, adding it here is the whole of
 * the wiring — there is a test that every name in this table exists on disk.
 */
export const OBSTACLE_ART: Record<ObstacleKindId, readonly string[]> = {
  [ObstacleKind.Tree]: ['field_copse_a', 'field_copse_b', 'field_copse_c'],
  [ObstacleKind.Rock]: [
    'field_boulder_a',
    'field_boulder_b',
    'field_rocks_a',
    'field_rocks_b',
    'field_cairn_a',
  ],
  [ObstacleKind.Thicket]: [
    'field_scrub_a',
    'field_scrub_b',
    'field_deadfall_a',
    'field_deadfall_b',
    'field_stakes_a',
    'field_bones_a',
    'field_stump_a',
  ],
};

/** The still this obstacle wears. Pure in the index, so it never flickers. */
export function obstacleArt(o: FieldObstacle, i: number): string {
  const set: readonly string[] =
    OBSTACLE_ART[o.kind as ObstacleKindId] ?? OBSTACLE_ART[ObstacleKind.Tree];
  return set[(set.length * hash2(i, o.kind, SALT_VARIANT)) | 0];
}

/**
 * Where the ground line sits in a `field_*` still, as a fraction of its height.
 *
 * The generator authors these at 192px with `groundY: 156`. It is written as a
 * fraction rather than as two numbers so a re-bake at a different resolution
 * needs no change here; it is written as a constant rather than read off the
 * texture because a texture cannot say which of its rows is the floor.
 */
export const PROP_GROUND = 156 / 192;

/**
 * How wide to draw the *box* of an obstacle of radius `r`, in tiles.
 *
 * This is not `2 × r`, and the first cut of this file made exactly that
 * mistake: it drew every prop at twice its blocking radius and a field came up
 * looking like scattered smudges. **A `field_*` still is mostly transparent
 * padding.** The painted content was measured across all fifteen — an image
 * loaded into a canvas and its alpha bounds walked — and it occupies between
 * 34% and 55% of the box width, mean **0.431**. So a prop drawn in a box of
 * `2 × r` puts a *painted* thing of about `0.86 × r` on the ground, which is
 * well under half the rock the simulation says is there.
 *
 * The factor is therefore `2 × oversail / fill` = `2 × 1.2 / 0.431` ≈ 5.6. The
 * oversail term is deliberate: the circle is what *blocks*, and a copse whose
 * leaves stopped exactly where its trunks do reads as a bush.
 *
 * One constant can only serve every prop because the *generator* was made to
 * cooperate: `props.mjs` draws field scatter to a constant reach rather than
 * at a constant scale, precisely so that a cairn and a copse blocking the same
 * three-tile circle come out the same size on the ground. Before that change
 * the fill ran 0.23 to 0.69 and no single number could have been right.
 *
 * If the props are re-baked at a different padding this has to be re-measured;
 * there is no way for the renderer to know it from the texture.
 */
export const PROP_SPREAD = 5.6;
